import { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Shield, Pencil, Eye, EyeOff, SquarePen, ChevronRight, SlidersHorizontal } from 'lucide-react';
import { getAdminSession, ADMIN_SECTIONS, SUB_SECTIONS, defaultSubPermissions } from '@/lib/auth';
import { ADMIN_USERS } from '@/constants/testIds';

const LEVELS = [
    { value: 'none', label: 'No access', icon: EyeOff },
    { value: 'view', label: 'Viewer', icon: Eye },
    { value: 'edit', label: 'Editor', icon: SquarePen },
];

const allSet = (level) => Object.fromEntries(ADMIN_SECTIONS.map((s) => [s.key, level]));
const DEFAULT_PERMS = allSet('view');

// ---------- Permission matrix (shared by create + edit dialogs) ----------
const PermissionMatrix = ({ value, onChange, subValue, onSubChange }) => {
    const [expanded, setExpanded] = useState({});
    const toggleExpand = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

    const setSub = (section, subKey, checked) => {
        const next = { ...(subValue || {}) };
        next[section] = { ...(next[section] || {}), [subKey]: checked };
        onSubChange(next);
    };
    const setAllSub = (section, checked) => {
        const next = { ...(subValue || {}) };
        next[section] = Object.fromEntries(SUB_SECTIONS[section].map((s) => [s.key, checked]));
        onSubChange(next);
    };

    return (
        <div className="rounded-lg border border-border/60">
            <div className="flex items-center justify-between border-b border-border/60 bg-muted/40 px-3 py-2">
                <span className="text-xs font-medium text-muted-foreground">Section access</span>
                <div className="flex gap-1">
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid="perm-all-view" onClick={() => onChange(allSet('view'))}>
                        All viewer
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid="perm-all-edit" onClick={() => onChange(allSet('edit'))}>
                        All editor
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" data-testid="perm-all-none" onClick={() => onChange(allSet('none'))}>
                        Clear
                    </Button>
                </div>
            </div>
            <div className="divide-y divide-border/60">
                {ADMIN_SECTIONS.map((s) => {
                    const subItems = SUB_SECTIONS[s.key];
                    const level = value[s.key] || 'none';
                    const hasSub = !!subItems && level !== 'none';
                    const isOpen = !!expanded[s.key];
                    const secSub = (subValue && subValue[s.key]) || {};
                    const visibleCount = subItems ? subItems.filter((it) => secSub[it.key] !== false).length : 0;
                    return (
                        <div key={s.key} data-testid={`perm-row-${s.key}`}>
                            <div className="flex items-center justify-between gap-2 px-3 py-2">
                                <div className="flex items-center gap-1.5">
                                    {hasSub ? (
                                        <button
                                            type="button"
                                            onClick={() => toggleExpand(s.key)}
                                            className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-accent"
                                            data-testid={`perm-expand-${s.key}`}
                                            aria-label={`Toggle sub-access for ${s.label}`}
                                        >
                                            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                                        </button>
                                    ) : (
                                        <span className="inline-block w-6" />
                                    )}
                                    <span className="text-sm">{s.label}</span>
                                    {hasSub && (
                                        <Badge variant="outline" className="ml-1 gap-1 px-1.5 py-0 text-[10px] text-muted-foreground">
                                            <SlidersHorizontal className="h-2.5 w-2.5" />
                                            {visibleCount}/{subItems.length}
                                        </Badge>
                                    )}
                                </div>
                                <ToggleGroup
                                    type="single"
                                    size="sm"
                                    variant="outline"
                                    value={level}
                                    onValueChange={(v) => { if (v) onChange({ ...value, [s.key]: v }); }}
                                >
                                    {LEVELS.map((l) => (
                                        <ToggleGroupItem
                                            key={l.value}
                                            value={l.value}
                                            aria-label={`${s.label}: ${l.label}`}
                                            data-testid={`perm-${s.key}-${l.value}`}
                                            className="h-7 gap-1 px-2 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                                        >
                                            <l.icon className="h-3 w-3" />
                                            <span className="hidden sm:inline">{l.label}</span>
                                        </ToggleGroupItem>
                                    ))}
                                </ToggleGroup>
                            </div>
                            {hasSub && isOpen && (
                                <div className="border-t border-dashed border-border/50 bg-muted/20 px-3 py-2 pl-10" data-testid={`perm-sub-${s.key}`}>
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Show these to the user</span>
                                        <div className="flex gap-1">
                                            <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setAllSub(s.key, true)}>All</Button>
                                            <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setAllSub(s.key, false)}>None</Button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                                        {subItems.map((it) => (
                                            <label key={it.key} className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`perm-sub-${s.key}-${it.key}`}>
                                                <Checkbox
                                                    checked={secSub[it.key] !== false}
                                                    onCheckedChange={(c) => setSub(s.key, it.key, !!c)}
                                                />
                                                {it.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Compact summary shown in the table
const AccessSummary = ({ admin }) => {
    if (admin.role === 'super_admin') {
        return <Badge className="gap-1"><Shield className="h-3 w-3" /> Super admin</Badge>;
    }
    const perms = admin.permissions || DEFAULT_PERMS;
    const editable = ADMIN_SECTIONS.filter((s) => perms[s.key] === 'edit');
    const viewable = ADMIN_SECTIONS.filter((s) => perms[s.key] === 'view');
    if (!editable.length && !viewable.length) {
        return <Badge variant="secondary" className="text-muted-foreground">No access</Badge>;
    }
    return (
        <div className="flex max-w-xs flex-wrap gap-1" data-testid={`access-summary-${admin.id}`}>
            {editable.map((s) => (
                <Badge key={s.key} className="gap-1 px-1.5 py-0 text-[10px]"><SquarePen className="h-2.5 w-2.5" />{s.label}</Badge>
            ))}
            {viewable.map((s) => (
                <Badge key={s.key} variant="outline" className="gap-1 px-1.5 py-0 text-[10px]"><Eye className="h-2.5 w-2.5" />{s.label}</Badge>
            ))}
        </div>
    );
};

export default function AdminUsersPage() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'viewer', permissions: { ...DEFAULT_PERMS }, sub_permissions: defaultSubPermissions() });
    const [editTarget, setEditTarget] = useState(null); // admin being edited
    const [editForm, setEditForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const me = getAdminSession()?.admin;

    const refresh = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/users');
            setAdmins(data.admins || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post('/admin/users', form);
            toast.success('Admin created');
            setForm({ name: '', email: '', password: '', role: 'viewer', permissions: { ...DEFAULT_PERMS }, sub_permissions: defaultSubPermissions() });
            setAddOpen(false);
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed');
        } finally {
            setSaving(false);
        }
    };

    const openEdit = (a) => {
        setEditTarget(a);
        setEditForm({
            name: a.name || '',
            role: a.role,
            password: '',
            permissions: { ...(a.permissions || DEFAULT_PERMS) },
            sub_permissions: { ...defaultSubPermissions(), ...(a.sub_permissions || {}) },
        });
    };

    const submitEdit = async (e) => {
        e.preventDefault();
        if (!editTarget) return;
        setSaving(true);
        try {
            const payload = { name: editForm.name, role: editForm.role };
            if (editForm.password) payload.password = editForm.password;
            if (editForm.role !== 'super_admin') {
                payload.permissions = editForm.permissions;
                payload.sub_permissions = editForm.sub_permissions;
            }
            await api.patch(`/admin/users/${editTarget.id}`, payload);
            toast.success('Access updated');
            setEditTarget(null);
            refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed');
        } finally {
            setSaving(false);
        }
    };

    const del = async (a) => {
        if (!window.confirm(`Delete admin ${a.email}?`)) return;
        try {
            await api.delete(`/admin/users/${a.id}`);
            toast.success('Deleted');
            refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed');
        }
    };

    const isBootstrap = (a) => a.id === 'super-admin';

    return (
        <AdminLayout
            title="Admin users"
            subtitle="Control exactly what each admin can see and do"
            actions={(
                <Button onClick={() => setAddOpen(true)} data-testid={ADMIN_USERS.add}>
                    <Plus className="mr-2 h-4 w-4" /> Add admin
                </Button>
            )}
        >
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Access</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {admins.map((a) => (
                                        <TableRow key={a.id} data-testid={`admin-user-row-${a.email}`}>
                                            <TableCell className="text-sm font-medium">
                                                {a.name}
                                                {a.id === me?.id && <span className="ml-1.5 text-xs text-muted-foreground">(you)</span>}
                                            </TableCell>
                                            <TableCell className="text-sm">{a.email}</TableCell>
                                            <TableCell><AccessSummary admin={a} /></TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {!isBootstrap(a) && (
                                                        <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={ADMIN_USERS.edit} aria-label={`Edit ${a.email}`}>
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {a.id !== me?.id && !isBootstrap(a) && (
                                                        <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => del(a)} data-testid={ADMIN_USERS.delete} aria-label={`Delete ${a.email}`}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ---------- Create admin ---------- */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display">Add admin</DialogTitle>
                        <DialogDescription>Pick a role, then fine-tune what they can see and edit.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <div className="max-h-[60vh] overflow-y-auto pr-2">
                            <div className="space-y-3 px-0.5 pb-1">
                                <div>
                                    <Label>Name</Label>
                                    <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="admin-user-name-input" />
                                </div>
                                <div>
                                    <Label>Email</Label>
                                    <Input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="admin-user-email-input" />
                                </div>
                                <div>
                                    <Label>Password</Label>
                                    <Input required minLength={8} type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} data-testid="admin-user-password-input" />
                                    <p className="mt-1 text-xs text-muted-foreground">Min 8 characters. You'll need to share this password with the user directly.</p>
                                </div>
                                <div>
                                    <Label>Role</Label>
                                    <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                                        <SelectTrigger data-testid="admin-user-role-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="viewer">Custom access (choose per section)</SelectItem>
                                            <SelectItem value="super_admin">Super admin (full access)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {form.role !== 'super_admin' && (
                                    <PermissionMatrix
                                        value={form.permissions}
                                        onChange={(p) => setForm((f) => ({ ...f, permissions: p }))}
                                        subValue={form.sub_permissions}
                                        onSubChange={(sp) => setForm((f) => ({ ...f, sub_permissions: sp }))}
                                    />
                                )}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={saving} data-testid={ADMIN_USERS.save}>
                                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Create
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ---------- Edit admin ---------- */}
            <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
                <DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display">Edit access — {editTarget?.email}</DialogTitle>
                        <DialogDescription>Changes take effect the next time they load a page. No re-login needed.</DialogDescription>
                    </DialogHeader>
                    {editForm && (
                        <form onSubmit={submitEdit} className="space-y-3">
                            <div className="max-h-[60vh] overflow-y-auto pr-2">
                                <div className="space-y-3 px-0.5 pb-1">
                                    <div>
                                        <Label>Name</Label>
                                        <Input required value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="admin-user-edit-name-input" />
                                    </div>
                                    <div>
                                        <Label>New password (optional)</Label>
                                        <Input type="password" minLength={8} placeholder="Leave blank to keep current" value={editForm.password} onChange={(e) => setEditForm((f) => ({ ...f, password: e.target.value }))} data-testid="admin-user-edit-password-input" />
                                    </div>
                                    <div>
                                        <Label>Role</Label>
                                        <Select
                                            value={editForm.role}
                                            onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}
                                            disabled={editTarget?.id === me?.id}
                                        >
                                            <SelectTrigger data-testid="admin-user-edit-role-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="viewer">Custom access (choose per section)</SelectItem>
                                                <SelectItem value="super_admin">Super admin (full access)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {editTarget?.id === me?.id && (
                                            <p className="mt-1 text-xs text-muted-foreground">You cannot change your own role.</p>
                                        )}
                                    </div>
                                    {editForm.role !== 'super_admin' && (
                                        <PermissionMatrix
                                            value={editForm.permissions}
                                            onChange={(p) => setEditForm((f) => ({ ...f, permissions: p }))}
                                            subValue={editForm.sub_permissions}
                                            onSubChange={(sp) => setEditForm((f) => ({ ...f, sub_permissions: sp }))}
                                        />
                                    )}
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="ghost" onClick={() => setEditTarget(null)}>Cancel</Button>
                                <Button type="submit" disabled={saving} data-testid="admin-users-edit-save">
                                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                    Save changes
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}
